/**
 * Pure helpers for the weekly rota fold-out — NO firebase import, so tests
 * exercise them without the build-defined globals (the side-effects-upstream
 * -of-test-seams rule). rota-week.ts owns the fetching and rendering.
 */

export interface RotaShift {
  start: string;
  end: string;
  practitionerId?: string;
  status?: string;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Monday 00:00 UTC of the week `offsetWeeks` from the week containing `now`. */
export function weekStartOf(now: Date, offsetWeeks: number): Date {
  const utcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const dow = new Date(utcMidnight).getUTCDay(); // 0=Sun..6=Sat
  const sinceMonday = (dow + 6) % 7;
  return new Date(utcMidnight - sinceMonday * DAY_MS + offsetWeeks * 7 * DAY_MS);
}

/** The 7 ISO dates (YYYY-MM-DD) of the week starting at `weekStart`. */
export function weekDays(weekStart: Date): string[] {
  return Array.from({ length: 7 }, (_, i) =>
    new Date(weekStart.getTime() + i * DAY_MS).toISOString().slice(0, 10));
}

/** Shifts grouped by ISO day, keeping only real shifts, in start order. */
export function bucketByDay(shifts: RotaShift[], days: string[]): Map<string, RotaShift[]> {
  const buckets = new Map<string, RotaShift[]>(days.map((d) => [d, []]));
  for (const s of shifts) {
    if (!s.start || !s.end || s.status === "entered-in-error") continue;
    buckets.get(s.start.slice(0, 10))?.push(s);
  }
  for (const list of buckets.values()) list.sort((a, b) => a.start.localeCompare(b.start));
  return buckets;
}

/** "08:00" from an ISO timestamp (UTC clock — matches the sync's storage). */
export function fmtTime(iso: string): string {
  return iso.slice(11, 16) || "—";
}

/** "Mon 8 Sep – Sun 14 Sep 2026" for the week's day list. */
export function fmtWeekLabel(days: string[]): string {
  const f = (isoDay: string, withYear: boolean) => {
    const d = new Date(`${isoDay}T00:00:00Z`);
    const opts: Intl.DateTimeFormatOptions = {
      weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
      ...(withYear ? { year: "numeric" } : {}),
    };
    return d.toLocaleDateString("en-GB", opts);
  };
  return `${f(days[0], false)} – ${f(days[6], true)}`;
}


// ── time-strip geometry (v0.5) ──────────────────────────────────────────
// Each day row is a horizontal ruler from WINDOW_START to WINDOW_END (care
// visits overwhelmingly live in 06:00–22:00); shifts are proportional
// blocks on it. Pure math so the tests own the edge cases.

export const WINDOW_START_MIN = 6 * 60;   // 06:00
export const WINDOW_END_MIN = 22 * 60;    // 22:00
const WINDOW_SPAN = WINDOW_END_MIN - WINDOW_START_MIN;

/** Minutes past UTC midnight for an ISO timestamp, or null if unparseable. */
export function minutesOfDay(iso: string): number | null {
  const h = Number(iso.slice(11, 13));
  const m = Number(iso.slice(14, 16));
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

export interface BlockGeometry {
  leftPct: number;
  widthPct: number;
  /** True when the shift spills past a window edge (early start, late end,
   * or a sleep-in crossing midnight — rendered flush to the edge). */
  clamped: boolean;
}

/**
 * Where a shift sits on its day's ruler. A shift wholly outside the window
 * returns null (nothing to draw); a midnight-crossing end (end "before"
 * start) renders to the window's right edge, clamped.
 */
export function blockGeometry(day: string, start: string, end: string): BlockGeometry | null {
  if (start.slice(0, 10) !== day) return null;
  const s = minutesOfDay(start);
  let e = minutesOfDay(end);
  if (s === null || e === null) return null;
  let clamped = false;
  if (end.slice(0, 10) !== day || e <= s) { e = WINDOW_END_MIN; clamped = true; }
  const s2 = Math.max(s, WINDOW_START_MIN);
  const e2 = Math.min(e, WINDOW_END_MIN);
  if (s2 !== s || e2 !== e) clamped = true;
  if (e2 <= s2) return null; // wholly outside the window
  return {
    leftPct: ((s2 - WINDOW_START_MIN) / WINDOW_SPAN) * 100,
    widthPct: ((e2 - s2) / WINDOW_SPAN) * 100,
    clamped,
  };
}

/** The now-line's offset on today's ruler, or null when outside the window. */
export function nowOffsetPct(now: Date): number | null {
  const mins = now.getUTCHours() * 60 + now.getUTCMinutes();
  if (mins < WINDOW_START_MIN || mins > WINDOW_END_MIN) return null;
  return ((mins - WINDOW_START_MIN) / WINDOW_SPAN) * 100;
}
